import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './OpenForm.css';
import CrmUtils from '../../services/crm-utils/CrmUtils';
import CityAutocomplete from '../../components/city-autocomplete/CityAutocomplete';
import GlobalDesignedCheckbox from '../../components/global/GlobalDesignedCheckbox';

const activityTypesDict = {
	PackingFoodDonationGear: "אריזת מזון וציוד לתרומה",
	DistributingFoodDonationGear: "חלוקת מזון וציוד לתרומה",
	TransportEquipmentMedicinesPatients: "שינוע ציוד, תרופות וחולים",
	FacilitationInstruction: "הפעלה והדרכה",
	Environment: "איכות הסביבה",
	Animals: "בעלי חיים",
	DIY: "עשו זאת בעצמכם",
	Other: "אחר",
};

function OpenForm() {
	const location = useLocation();

	// Form state
	const [formType, setFormType] = useState('individual'); // 'individual', 'group', 'company'
	const [formSubmitted, setFormSubmitted] = useState(false);
	const [submitError, setSubmitError] = useState(false);

	// Common fields
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [phone, setPhone] = useState('');
	const [email, setEmail] = useState('');
	const [city, setCity] = useState(null);
	const [selectedActivities, setSelectedActivities] = useState([]);
	const [additionalInfo, setAdditionalInfo] = useState('');
	const [newsletter, setNewsletter] = useState(false);

	// Group/Company specific fields
	const [groupName, setGroupName] = useState('');
	const [companyName, setCompanyName] = useState('');
	const [volunteersCount, setVolunteersCount] = useState(1);

	// Individual specific
	const [age, setAge] = useState('');

	// Validation state
	const [validationErrors, setValidationErrors] = useState([]);
	const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
	const errorMessageRef = useRef(null);

	// Validation patterns
	const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	const israeliPhonePattern = /^(0[23489]\d{7}|05\d{8})$/; // Landline: 02/03/04/08/09 + 7 digits, Mobile: 05X + 8 digits

	// Form validation function
	const validateForm = () => {
		const errors = [];

		// Validate group name (for group/company)
		if (formType !== 'individual') {
			if (!groupName || groupName.trim() === '') {
				errors.push({ field: 'groupName', label: 'שם קבוצה' });
			}
		}

		// Validate company name (for company only)
		if (formType === 'company') {
			if (!companyName || companyName.trim() === '') {
				errors.push({ field: 'companyName', label: 'שם חברה' });
			}
		}

		// Validate volunteers count (for group/company)
		if (formType !== 'individual') {
			if (!volunteersCount || parseInt(volunteersCount) < 1) {
				errors.push({ field: 'volunteersCount', label: 'מספר מתנדבים' });
			}
		}

		// Validate first name
		if (!firstName || firstName.trim() === '') {
			errors.push({ field: 'firstName', label: 'שם פרטי' });
		}

		// Validate last name
		if (!lastName || lastName.trim() === '') {
			errors.push({ field: 'lastName', label: 'שם משפחה' });
		}

		// Validate city
		if (!city) {
			errors.push({ field: 'city', label: 'איפה תרצו להתנדב?' });
		}

		// Validate email
		if (!email || !emailPattern.test(email)) {
			errors.push({ field: 'email', label: 'אימייל' });
		}

		// Validate phone
		const cleanPhone = phone.replace(/[-\s]/g, ''); // Remove dashes and spaces
		if (!cleanPhone || !israeliPhonePattern.test(cleanPhone)) {
			errors.push({ field: 'phone', label: 'טלפון' });
		}

		// Validate activity types - at least one must be selected
		if (selectedActivities.length === 0) {
			errors.push({ field: 'selectedActivities', label: 'איך תרצו לעשות טוב?' });
		}

		// Validate additional info - must not be empty
		if (!additionalInfo || additionalInfo.trim() === '') {
			errors.push({ field: 'additionalInfo', label: 'יש עוד משהו שחשוב שנדע?' });
		}

		setValidationErrors(errors);
		return errors.length === 0;
	};

	// Check if a field has validation error
	const hasError = (fieldName) => validationErrors.some(e => e.field === fieldName);

	// Error style for invalid fields
	const errorStyle = { borderColor: '#FF0000', color: '#FF0000' };

	// Re-validate on field changes after first submit attempt
	useEffect(() => {
		if (hasAttemptedSubmit) {
			validateForm();
		}
	}, [hasAttemptedSubmit, formType, groupName, companyName, volunteersCount, firstName, lastName, city, email, phone, selectedActivities, additionalInfo]);

	// Desktop detection
	const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);

	useEffect(() => {
		const handleResize = () => setIsDesktop(window.innerWidth > 768);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Reset form submitted state when navigating to this page
	useEffect(() => {
		setFormSubmitted(false);
	}, [location.key]);

	const getFormType = () => {
		const typeMap = {
			'individual': 'Individual',
			'group': 'Group',
			'company': 'Corporate'
		};
		return typeMap[formType] || 'Individual';
	};

	const handleActivityToggle = (activityKey) => {
		setSelectedActivities(prev => {
			if (prev.includes(activityKey)) {
				return prev.filter(a => a !== activityKey);
			} else {
				return [...prev, activityKey];
			}
		});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSubmitError(false);

		// Mark that user has attempted to submit
		setHasAttemptedSubmit(true);

		// Validate form before submission
		if (!validateForm()) {
			// Scroll to error message after state update (with offset)
			setTimeout(() => {
				if (errorMessageRef.current) {
					const yOffset = -100;
					const y = errorMessageRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
					window.scrollTo({ top: y, behavior: 'smooth' });
				}
			}, 100);
			return;
		}

		const formData = {
			volunteerData: {
				name: `${firstName} ${lastName}`.trim(),
				type: getFormType(),
				status: 'external',
				confirmationStatus: 'undone',
				numOfPeople: formType !== 'individual' ? parseInt(volunteersCount) || 1 : 1,
				budget: 0,
				triedToBeReached: 0,
				year: new Date().getFullYear(),
				address: {
					address: 'ללא כתובת',
					city: {
						_id: city?.id || ''
					},
					geo: {
						latitude: 0,
						longitude: 0
					}
				},
				preferences: {
					groupName: formType !== 'individual' ? groupName : '',
					companyName: formType === 'company' ? companyName : '',
					activityTypes: selectedActivities,
					newsletter: newsletter,
					age: formType === 'individual' ? age : ''
				},
				notes: additionalInfo ? [{ text: additionalInfo, createdAt: new Date().toISOString() }] : [],
				description: additionalInfo
			},
			contactData: {
				contact: {
					name: `${firstName} ${lastName}`.trim(),
					email: email,
					phone: phone
				},
				isPrimary: true
			}
		};

		try {
			const response = await CrmUtils.createVolunteer(formData);
			console.log('Volunteer created:', response);
			setFormSubmitted(true);
			// Reset validation state
			setValidationErrors([]);
			setHasAttemptedSubmit(false);
			// Scroll to top after successful submission
			window.scrollTo({ top: 0, behavior: 'smooth' });
		} catch (error) {
			console.error('Error creating volunteer:', error);
			setSubmitError('אירעה שגיאה בשליחת הטופס. אנא נסו שנית.');
		}
	};

	const resetForm = () => {
		setFormSubmitted(false);
		setFirstName('');
		setLastName('');
		setPhone('');
		setEmail('');
		setCity(null);
		setSelectedActivities([]);
		setAdditionalInfo('');
		setNewsletter(false);
		setGroupName('');
		setCompanyName('');
		setVolunteersCount(1);
		setAge('');
	};

	if (formSubmitted) {
		return (
			<div id="main" role="main">
				<section className="open-form__section">
					<div className="content-container">
						<div className="open-form__success" style={{justifyContent: "center !important", display: "flex", flexDirection: "column"}}>
							<h2>תודה!</h2>
							<p>הטופס נשלח בהצלחה.</p>
							<p>ניצור איתך קשר בהקדם.</p>
							<button className="btn btn-magenta" onClick={resetForm} style={{ alignSelf: 'center', marginTop: '20px', width: '245px' }}>שליחת טופס נוסף</button>
						</div>
					</div>
				</section>
			</div>
		);
	}

	return (
		<div id="main" role="main">
			<section className="open-form__section">
				<div className="content-container">
					<h1 className="open-form__title">מצאו את הדרך שלכם לעשות טוב</h1>
					<div className="open-form__wrapper">
					

						<form className={`open-form${isDesktop ? ' signup-form' : ''}`} onSubmit={handleSubmit} style={validationErrors.length > 0 ? { paddingTop: 0 } : {}}>
							<div className="open-form__intro">
								<p>קבוצות? מלאו את הטופס ונחזור אליכם.</p>
								<p>יחידים? כל הזדמנויות ההתנדבות נמצאות באתר, אם בכל זאת תצטרכו עזרה מאיתנו מלאו את הטופס, ונחזור אליכם.</p>
							</div>
							{/* Validation Error Message */}
							{validationErrors.length > 0 && (
								<div ref={errorMessageRef} className="validation-error-message" style={{ color: '#FF0000', marginBottom: '15px' }}>
									<p>הטופס לא נשלח משום שחלק מהפרטים חסרים: {validationErrors.map(e => e.label).join(', ')}</p>
								</div>
							)}
							{/* Form Type Selector */}
							<p className="form-question">מי אתם?</p>
							<div className="form-type-selector">
								<button
									type="button"
									className={`type-btn ${formType === 'company' ? 'active' : ''}`}
									onClick={() => setFormType('company')}
								>
									חברה
								</button>
								<button
									type="button"
									className={`type-btn ${formType === 'group' ? 'active' : ''}`}
									onClick={() => setFormType('group')}
								>
									קבוצה
								</button>
								<button
									type="button"
									className={`type-btn ${formType === 'individual' ? 'active' : ''}`}
									onClick={() => setFormType('individual')}
								>
									אדם פרטי
								</button>
							</div>

							{/* Group/Company Specific Fields */}
							{formType !== 'individual' && (
									<><h3 className="section-title">פרטי הקבוצה</h3>
									<div className="form-field">
										<label className='ltr' style={hasError('groupName') ? errorStyle : {}}>שם קבוצה*</label>
										<input
											type="text"
											value={groupName}
											onChange={(e) => setGroupName(e.target.value)}
											style={hasError('groupName') ? errorStyle : {}}
										/>
									</div>

									{formType === 'company' && (
										<div className="form-field">
											<label className='ltr' style={hasError('companyName') ? errorStyle : {}}>שם חברה*</label>
											<input
												type="text"
												value={companyName}
												onChange={(e) => setCompanyName(e.target.value)}
												style={hasError('companyName') ? errorStyle : {}}
											/>
										</div>
									)}

									<div className="form-field">
										<label className='ltr' style={hasError('volunteersCount') ? errorStyle : {}}>מספר מתנדבים*</label>
										<input
											type="number"
											value={volunteersCount}
											onChange={(e) => setVolunteersCount(parseInt(e.target.value) || 1)}
											min="1"
											style={hasError('volunteersCount') ? errorStyle : {}}
										/>
									</div>
								</>
							)}

							{/* Section Title - changes based on form type */}
							<h3 className="section-title">
								{formType === 'individual' ? 'פרטים אישיים' : 'פרטי איש קשר'}
							</h3>

							{/* Common Contact/Personal Fields */}
							<div className="form-field">
								<label className='ltr' style={hasError('firstName') ? errorStyle : {}}>שם פרטי*</label>
								<input
									type="text"
									value={firstName}
									onChange={(e) => setFirstName(e.target.value)}
									style={hasError('firstName') ? errorStyle : {}}
								/>
							</div>

							<div className="form-field">
								<label className='ltr' style={hasError('lastName') ? errorStyle : {}}>שם משפחה*</label>
								<input
									type="text"
									value={lastName}
									onChange={(e) => setLastName(e.target.value)}
									style={hasError('lastName') ? errorStyle : {}}
								/>
							</div>

							<div className="form-field">
								<label className='ltr' style={hasError('city') ? errorStyle : {}}>איפה תרצו להתנדב?*</label>
								<CityAutocomplete
									value={city}
									onChange={setCity}
									placeholder="בחרו עיר"
									hasError={hasError('city')}
								/>
							</div>

							<div className="form-field">
								<label className='ltr' style={hasError('email') ? errorStyle : {}}>אימייל*</label>
								<input
									type="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									style={hasError('email') ? errorStyle : {}}
								/>
							</div>

							<div className="form-field">
								<label className='ltr' style={hasError('phone') ? errorStyle : {}}>טלפון*</label>
								<input
									type="tel"
									value={phone}
									onChange={(e) => setPhone(e.target.value)}
									style={hasError('phone') ? errorStyle : {}}
								/>
							</div>

							{/* Activity Types - for all form types */}
							<div className="form-field activity-field">
								<label className="activity-label ltr" style={hasError('selectedActivities') ? errorStyle : {}}>?איך תרצו לעשות טוב*</label>
								<div className="activity-options">
									{Object.entries(activityTypesDict).map(([key, label]) => (
										<GlobalDesignedCheckbox
											key={key}
											label={label}
											checked={selectedActivities.includes(key)}
											onChange={() => handleActivityToggle(key)}
											name={key}
										/>
									))}
								</div>
							</div>

							{/* Additional Info */}
							<div className="form-field">
								<label className='ltr' style={hasError('additionalInfo') ? errorStyle : {}}>?יש עוד משהו שחשוב שנדע*</label>
								<textarea
									value={additionalInfo}
									onChange={(e) => setAdditionalInfo(e.target.value)}
									rows={5}
									style={hasError('additionalInfo') ? errorStyle : {}}
								/>
							</div>

							{/* Newsletter Checkbox */}
							<div className="checkbox-field">
								<GlobalDesignedCheckbox
									label="שלחו לי עוד דברים טובים מיום מעשים טובים, עמותת רוח טובה"
									checked={newsletter}
									onChange={(e) => setNewsletter(e.target.checked)}
									name="newsletter"
								/>
							</div>

							{/* Disclaimer */}
							<p className="disclaimer">*פעילות ההתנדבות הינה באחריותו הבלעדית של המתנדב</p>

							{/* Error message */}
							{submitError && (
								<div className="error-message">
									<p>{submitError}</p>
								</div>
							)}

							{/* Submit Button */}
							<button type="submit" className="btn btn-magenta submit-btn">הציעו לי מעשים טובים</button>
						</form>
					</div>
				</div>
			</section>
		</div>
	);
}

export default OpenForm;
